import { formatInTimeZone } from 'date-fns-tz';
import { MINOR_AGE, SYSTEM_ROLES, SYSTEM_ROLES_IDENTIFIERS } from '../config';
import {
  AccountUser,
  Member,
  MemberPlanElement,
  OrganizationElement,
} from '../features/account/types';

import { MessagesListContent } from '../types';

export const rolesFromFHIR4Identifier = (identifiers: fhir4.Identifier[]): string[] => {
  const roles: string[] = [];
  identifiers.forEach((identifier) => {
    if (identifier.system === SYSTEM_ROLES_IDENTIFIERS.ADMIN) {
      roles.push(SYSTEM_ROLES.ADMIN);
    }
    if (identifier.system === SYSTEM_ROLES_IDENTIFIERS.MANAGER) {
      roles.push(SYSTEM_ROLES.MANAGER);
    }
    if (identifier.system === SYSTEM_ROLES_IDENTIFIERS.PROVIDER) {
      roles.push(SYSTEM_ROLES.PROVIDER);
    }
  });
  if (roles.length == 0) {
    roles.push(SYSTEM_ROLES.PATIENT);
  }
  return roles;
};
export function isNullOrUndefined<T = any>(val: T) {
  return val === null || val === undefined;
}

/**
 * Parses a date-only string (YYYY-MM-DD) as a local date to avoid timezone issues.
 * When using new Date("YYYY-MM-DD"), JavaScript interprets it as UTC, which can
 * cause the date to shift backward by one day in timezones behind UTC.
 *
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object representing the date in local timezone
 */
export function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
}

export const downloadAttachment = (url: string) => {
  if (!url) {
    return;
  }

  const fileName = url.substring(url.lastIndexOf('/') + 1, url.lastIndexOf('?')).replace('%', ' ');
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
};

export const usernameFromFHIR4HumanName = (name?: fhir4.HumanName): string | undefined => {
  if (!name) return undefined;
  if (name.text && name.text.length > 0) return name.text;

  const nameArr: string[] = [];

  if (name.prefix && name.prefix.length > 0) {
    const prefix = name.prefix
      .map((n) => n)
      .join(' ')
      .trim();
    nameArr.push(prefix);
  }
  if (name.given && name.given.length > 0) {
    const given = name.given[0].trim();
    nameArr.push(given);
  }
  if (name.given && name.given.length > 1) {
    let middleInitial = name.given[1].trim();
    if (middleInitial) {
      middleInitial = middleInitial.charAt(0).toUpperCase() + '.';
      nameArr.push(middleInitial);
    }
  }
  if (name.family && name.family.length > 0) {
    const family = name.family.trim();
    nameArr.push(family);
  }
  if (name.suffix && name.suffix.length > 0) {
    const suffix = name.suffix
      .map((s) => s)
      .join(', ')
      .trim();
    nameArr.push(suffix);
  }

  return nameArr.join(' ');
};

export function calculateAge(dateOfBirth: string | number): number {
  const dob: Date = new Date(dateOfBirth);
  const now: Date = new Date();

  let age: number = now.getFullYear() - dob.getFullYear();
  const monthDiff: number = now.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

export function calculateAgeFromDateObj(dateOfBirth: Date, timezone: string): number {
  const now: Date = new Date();
  const formattedTimeZoneString: string = formatInTimeZone(dateOfBirth, timezone, 'yyyy-MM-dd');
  const formattedTimeZoneDate: Date = new Date(formattedTimeZoneString);
  let age: number = now.getFullYear() - formattedTimeZoneDate.getFullYear();
  const monthDiff: number = now.getMonth() - formattedTimeZoneDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < formattedTimeZoneDate.getDate())) {
    age--;
  }

  return age;
}

export function formatGender(gender: string) {
  if (!gender.length) return '';
  const firstChar = gender[0].toUpperCase();
  switch (firstChar) {
    case 'M':
      return 'M';
    case 'F':
      return 'F';
    case 'O':
      return 'O';
    default:
      return '';
  }
}

export function getUserPlans(organization: OrganizationElement[] | undefined): MemberPlanElement[] {
  if (!organization) return [];

  const userPlans = organization
    .flatMap(({ memberPlan, name }) => {
      return memberPlan?.map((memberPlan) => ({
        ...memberPlan,
        organizationName: name,
      }));
    })
    .filter(Boolean);

  return userPlans;
}
export const parseMessagesData = (entries: any[]): MessagesListContent[] | any[] => {
  if (!entries || entries.length === 0) return [];
  return entries.map((entry: any) => {
    const { resource } = entry;
    return messageFromData(resource);
  });
};

export const messageFromData = (resource: any) => {
  return {
    id: resource.id,
    mailboxId: resource.mailboxId,
    messageId: resource.messageId,
    threadId: resource.threadId,
    status: resource.elementStatus,
    draft: resource.draft,
    message: resource.message,
    from: resource.from,
    to: resource.to,
    reference: resource.reference,
    read: resource.read,
    hasReply: resource.hasReply,
  };
};
export const parsePatientHouseholdData = (entry: Member): AccountUser | any => {
  if (!entry) return [];
  return PatientHouseholdFromData(entry);
};

export const PatientHouseholdFromData = (resource: any) => {
  const email =
    resource.telecom && resource.telecom.length > 0
      ? resource.telecom?.find((telecom: any) => telecom.system === 'email')?.value
      : '';
  const phoneNr = resource?.telecom?.find((telecom: any) => telecom.system === 'phone');

  const getOrganizationTitles = resource.organization?.length
    ? resource.organization.map((org: any) => {
        return org.name;
      })
    : ['Retail'];

  const age = resource?.birthDate ? calculateAge(resource.birthDate) : undefined;
  const isMinor = !isNullOrUndefined(age) ? (age as number) < MINOR_AGE : false;
  const isHouseHold = resource?.headOfHousehold != null;

  return {
    id: resource.id,
    uuid: resource.uuid,
    avatar: resource.avatar?.[0].url || '',
    name: usernameFromFHIR4HumanName(resource?.name?.[0]) || '',
    organizationTitle: getOrganizationTitles.join(', '),
    email,
    phoneNr: phoneNr?.value || '',
    countryCode: phoneNr?.countryCode || '',
    dob: resource?.birthDate || '',
    status: resource?.active || false,
    gender: resource?.gender ? resource?.gender.charAt(0).toUpperCase() : undefined,
    age,
    isMinor,
    isHouseHold,
    loginCount: resource?.loginCount,
  };
};
